import { useEffect, useState } from "react";
import { Download, RotateCcw, FileText, Check, Ban, Clock, Stamp, Printer, Share2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ShareSnapshot from "@/components/ShareSnapshot";

const STATUS_META = {
  ALLOWED:   { chip: "fc-chip-allowed",   icon: Check,  label: "Allowed" },
  BLOCKED:   { chip: "fc-chip-blocked",   icon: Ban,    label: "Blocked" },
  ESCALATED: { chip: "fc-chip-escalated", icon: Clock,  label: "Escalated" },
  APPROVED:  { chip: "fc-chip-approved",  icon: Stamp,  label: "Approved (co-signed)" },
};

export default function Evidence() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const data = await api.evidence();
    setReceipts(data.receipts || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (selected && receipts.length) {
      const updated = receipts.find(r => r.receipt_id === selected.receipt_id);
      if (updated) setSelected(updated);
    }
  }, [receipts]); // eslint-disable-line

  const onExport = async () => {
    const data = await api.exportEvidence();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `freightcierge-evidence-${Date.now()}.json`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Evidence pack exported");
  };

  const onReset = async () => {
    await api.resetEvidence();
    setSelected(null);
    await load();
    toast.success("Evidence pack cleared");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10 fc-fade-up">
      <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="fc-eyebrow mb-2">Evidence pack · assurance view</div>
          <h1 className="display text-[36px] leading-tight max-w-[40ch]">
            Every material action — actor, authority, basis, evidence, downstream effects.
          </h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button variant="outline" onClick={onReset} className="rounded-sm h-10" data-testid="reset-evidence-btn">
            <RotateCcw size={14} className="mr-2"/> Reset
          </Button>
          <Button variant="outline" onClick={() => window.open("/evidence/print", "_blank")} className="rounded-sm h-10" data-testid="download-pdf-btn">
            <Printer size={14} className="mr-2"/> Download PDF
          </Button>
          <ShareSnapshot enabledSifty={true} enabledRWI={false} />
          <Button onClick={onExport} className="bg-[var(--navy)] hover:bg-black rounded-sm h-10" data-testid="export-evidence-btn">
            <Download size={14} className="mr-2"/> Export JSON
          </Button>
        </div>
      </div>

      {loading && <div className="text-[var(--ink-soft)]">Loading…</div>}

      <div className="border border-[#FDE68A] bg-[#FFFBEB] p-3 mb-6 flex items-start gap-3" data-testid="evidence-simulated-banner">
        <AlertTriangle size={14} className="text-[var(--amber)] mt-0.5"/>
        <div className="text-[12px] text-[#7C5E10] max-w-[80ch]">
          <strong>Simulated demo recap — not KATLAS-signed.</strong> Receipts shown here are from the simulated walkthrough.
          For a live KATLAS-signed FreightCierge receipt, use <a href="/sandbox" className="fc-link-underline">the live sandbox</a>.
        </div>
      </div>

      {!loading && receipts.length === 0 && (
        <div className="fc-card p-12 text-center" data-testid="evidence-empty">
          <FileText size={28} className="mx-auto mb-4 text-[var(--ink-soft)]"/>
          <div className="display text-[20px] mb-2">No receipts yet</div>
          <p className="text-[var(--ink-soft)] text-sm max-w-md mx-auto">
            Run actions in the live sandbox. Every consequential decision will generate a signed receipt here.
          </p>
        </div>
      )}

      {!loading && receipts.length > 0 && (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5" data-testid="receipts-list">
            <div className="fc-card divide-y divide-[var(--line)]">
              {receipts.slice().reverse().map(r => {
                const meta = STATUS_META[r.status] || STATUS_META.ALLOWED;
                const Icon = meta.icon;
                const isActive = selected?.receipt_id === r.receipt_id;
                return (
                  <button
                    key={r.receipt_id}
                    onClick={() => setSelected(r)}
                    data-testid={`receipt-row-${r.receipt_id}`}
                    className={`w-full text-left p-5 transition-colors ${isActive ? "bg-[#F4F7FB]" : "hover:bg-[var(--paper)]"}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`fc-chip ${meta.chip}`}><Icon size={11}/> {meta.label}</span>
                      <span className="mono text-[11px] text-[var(--ink-soft)] ml-auto">{r.receipt_id}</span>
                    </div>
                    <div className="display text-[15px] leading-snug mb-1">{r.action_label}</div>
                    <div className="text-[12px] text-[var(--ink-soft)]">
                      {r.actor_role} · {r.actor_org} <span className="fc-divider-dot"/> {new Date(r.timestamp).toLocaleTimeString()}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7">
            {selected ? <ReceiptDetail r={selected}/> : (
              <div className="fc-card p-8 text-[var(--ink-soft)] text-sm">Select a receipt to see the full assurance detail.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiptDetail({ r }) {
  const meta = STATUS_META[r.status] || STATUS_META.ALLOWED;
  const Icon = meta.icon;
  return (
    <div className="fc-card p-7" data-testid="receipt-detail">
      <div className="flex items-center gap-2 mb-4">
        <span className={`fc-chip ${meta.chip}`}><Icon size={11}/> {meta.label}</span>
        <span className="mono text-[11px] text-[var(--ink-soft)] ml-auto">{r.receipt_id}</span>
      </div>
      <h2 className="display text-[24px] mb-1">{r.action_label}</h2>
      <p className="text-[13px] text-[var(--ink-soft)] mono">{new Date(r.timestamp).toUTCString()}</p>

      <Field label="Actor"        value={`${r.actor_role}`} sub={r.actor_org}/>
      <Field label="Authority used" value={r.authority_used}/>
      <Field label="Decision basis" value={r.decision_basis}/>
      <Field label="Linked event"  value={r.linked_event_id} mono/>
      {r.note && <Field label="Note" value={r.note}/>}

      <div className="fc-section-rule pt-4 mt-5">
        <div className="fc-eyebrow mb-2">Downstream notifications</div>
        <ul className="text-[13px] space-y-1">
          {r.downstream.map((d, i) => <li key={i}>· {d}</li>)}
        </ul>
      </div>

      <div className="fc-section-rule pt-4 mt-5">
        <div className="fc-eyebrow mb-2">Evidence references</div>
        <div className="flex flex-wrap gap-2">
          {r.evidence_refs.map((e, i) =>
            <span key={i} className="mono text-[11px] border border-[var(--line)] px-2 py-1 bg-[var(--paper)]">{e}</span>
          )}
        </div>
      </div>

      {r.approvals.length > 0 && (
        <div className="fc-section-rule pt-4 mt-5">
          <div className="fc-eyebrow mb-2">Approvals</div>
          {r.approvals.map((a, i) => (
            <div key={i} className="border border-[var(--line)] p-3 mb-2 text-[13px]">
              <div className="display">{a.approver_role}</div>
              <div className="text-[var(--ink-soft)] text-[12px]">{a.approver_org} · {a.authority_used}</div>
              {a.note && <div className="mt-1 text-[12px]">"{a.note}"</div>}
              <div className="mono text-[11px] text-[var(--ink-soft)] mt-1">{new Date(a.timestamp).toUTCString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, sub, mono }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-4 py-3 border-b border-[var(--line)] last:border-b-0">
      <div className="fc-eyebrow pt-0.5">{label}</div>
      <div>
        <div className={mono ? "mono text-[13px]" : "text-[14px]"}>{value}</div>
        {sub && <div className="text-[12px] text-[var(--ink-soft)] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
